import { differenceInMinutes, isSameDay, startOfDay, MINUTES_IN_DAY, MINUTES_IN_HOUR } from '@repo/date-time';
import { useMemo } from 'react';
import { ParkTimeContractType } from '@repo/domain';
import { InertiaSlider, DurationScale } from '@repo/ui';

import { MINIMUM_MINUTES_PICKABLE } from '@shared/constants/timeContract';

const LENGTH_OF_HOUR_ON_SCALE_PX: number = 96 as const;
const MAXIMUM_SLIDER_MINUTES: number = MINUTES_IN_DAY;
const DURATION_STEP_MINUTES: number = 5 as const;

interface Props {
  onSliderChange: (value: number) => void;
  value: number;
  timeContract: ParkTimeContractType;
  date: Date;
  startAt: Date;
}

export default function TimeContractSlider({ onSliderChange, value, timeContract, date, startAt }: Props) {
  const minimumTimeSelectable = useMemo<number>(() => {
    if (timeContract === ParkTimeContractType.FixedDuration) {
      return MINIMUM_MINUTES_PICKABLE;
    }
    if (isSameDay(date, startAt)) {
      // +1 is needed due to the way differenceInMinutes rounds minutes
      return differenceInMinutes(startAt, startOfDay(startAt)) + MINIMUM_MINUTES_PICKABLE + 1;
    }
    return 0;
  }, [date, timeContract, startAt]);

  // The slider emits 1-minute granularity; snap it to 5-minute steps so parking
  // durations are only pickable in 5-minute increments.
  const handleChange = (newValue: number) => {
    onSliderChange(Math.round(newValue / DURATION_STEP_MINUTES) * DURATION_STEP_MINUTES);
  };

  return (
    <>
      <InertiaSlider
        key={minimumTimeSelectable}
        onChange={handleChange}
        value={value}
        min={minimumTimeSelectable}
        max={MAXIMUM_SLIDER_MINUTES}
        ratio={LENGTH_OF_HOUR_ON_SCALE_PX / MINUTES_IN_HOUR}
        scale={<DurationScale minutes={MAXIMUM_SLIDER_MINUTES} />}
      />
    </>
  );
}
